import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { useNotificationContext } from '../../contexts/NotificationContext'

import IssuesCreator from './IssuesCreator'

interface IssueRow {
  id: string
  client_id: number
  client_name: string
  date: string
  issue_type: string
  status: string
  reporter_name: string
  reporter_phone: string
  location?: string
  street_address: string
  is_common_area: boolean
  is_external: boolean
  readonly: boolean
  author_name?: string
}

interface IssueDetail extends IssueRow {
  latitude?: string
  longitude?: string
  additional_info?: string
  images?: Array<{
    id: number
    filename: string
    original_filename: string
    file_path: string
    file_size: number
    mime_type: string
    description?: string
    uploaded_at: string
  }>
  status_history?: Array<{
    id: number
    old_status?: string
    new_status: string
    changed_by_name: string
    changed_at: string
  }>
  comments?: Array<{
    id: number
    user_id: number
    user_full_name: string
    content: string
    created_at: string
    updated_at?: string
  }>
}

interface Client { id: number; name: string }

interface IssuesListProps { token: string }

export default function IssuesList({ token }: IssuesListProps) {
  const [issues, setIssues] = useState<IssueRow[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showCreator, setShowCreator] = useState(false)
  const [editingIssueId, setEditingIssueId] = useState<string | undefined>(undefined)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewIssue, setPreviewIssue] = useState<IssueDetail | null>(null)
  const [comments, setComments] = useState<Array<{
    id: number
    user_id: number
    user_full_name: string
    content: string
    created_at: string
    updated_at?: string
  }>>([])
  const [showCommentForm, setShowCommentForm] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [commentLoading, setCommentLoading] = useState(false)

  // Email functionality
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [emailModalType, setEmailModalType] = useState<'update' | 'summary'>('update')
  const [emailUpdateType, setEmailUpdateType] = useState<'status_change' | 'comment_added'>('status_change')
  const [emailNewStatus, setEmailNewStatus] = useState('')
  const [emailCommentContent, setEmailCommentContent] = useState('')
  const [emailIssueId, setEmailIssueId] = useState('')
  const [showEmailFormModal, setShowEmailFormModal] = useState(false)
  const [emailFormData, setEmailFormData] = useState({
    to: '',
    subject: '',
    body: ''
  })
  const [emailSending, setEmailSending] = useState(false)

  // Obsługa gestów dotykowych dla mobilnych
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)

  // Funkcje obsługi gestów dotykowych
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > 50
    const isRightSwipe = distance < -50

    if (isLeftSwipe || isRightSwipe) {
      const scrollableElement = document.querySelector('.overflow-x-auto')
      if (scrollableElement) {
        const currentScroll = scrollableElement.scrollLeft
        const scrollAmount = 200

        if (isLeftSwipe) {
          scrollableElement.scrollLeft = currentScroll + scrollAmount
        } else {
          scrollableElement.scrollLeft = currentScroll - scrollAmount
        }
      }
    }
  }

  const { showSuccess, showError, showConfirm } = useNotificationContext()

  // Filtry - wartości w polach input
  const [clientFilterInput, setClientFilterInput] = useState<number | ''>('')
  const [dateFromFilterInput, setDateFromFilterInput] = useState('')
  const [dateToFilterInput, setDateToFilterInput] = useState('')
  const [issueTypeFilterInput, setIssueTypeFilterInput] = useState<string>('')
  const [statusFilterInput, setStatusFilterInput] = useState<string>('')
  const [searchFilterInput, setSearchFilterInput] = useState('')

  // Filtry - aktywne wartości
  const [clientFilter, setClientFilter] = useState<number | ''>('')
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [dateToFilter, setDateToFilter] = useState('')
  const [issueTypeFilter, setIssueTypeFilter] = useState<string>('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchFilter, setSearchFilter] = useState('')

  // Sortowanie
  const [sortBy, setSortBy] = useState('date')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')

  useEffect(() => {
    loadClients()
    loadIssues()
  }, [])

  useEffect(() => {
    loadIssues()
  }, [clientFilter, dateFromFilter, dateToFilter, issueTypeFilter, statusFilter, searchFilter, sortBy, sortOrder])

  const handleFilterClick = () => {
    setClientFilter(clientFilterInput)
    setDateFromFilter(dateFromFilterInput)
    setDateToFilter(dateToFilterInput)
    setIssueTypeFilter(issueTypeFilterInput)
    setStatusFilter(statusFilterInput)
    setSearchFilter(searchFilterInput)
  }

  const clearInputFilters = () => {
    setClientFilterInput('')
    setDateFromFilterInput('')
    setDateToFilterInput('')
    setIssueTypeFilterInput('')
    setStatusFilterInput('')
    setSearchFilterInput('')
  }

  const clearActiveFilters = () => {
    setClientFilter('')
    setDateFromFilter('')
    setDateToFilter('')
    setIssueTypeFilter('')
    setStatusFilter('')
    setSearchFilter('')
  }

  const clearFilters = () => {
    clearInputFilters()
    clearActiveFilters()
  }

  const loadClients = async () => {
    try {
      const response = await fetch('/api/clients/list', {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        const data = await response.json()
        setClients(data)
      }
    } catch (err) {
      console.error('Error loading clients:', err)
    }
  }

  const loadIssues = async () => {
    try {
      setLoading(true)
      setError('')
      const params = new URLSearchParams()
      if (clientFilter) params.append('client_id', clientFilter.toString())
      if (dateFromFilter) params.append('date_from', dateFromFilter)
      if (dateToFilter) params.append('date_to', dateToFilter)
      if (issueTypeFilter) params.append('issue_type', issueTypeFilter)
      if (statusFilter) params.append('status', statusFilter)
      if (searchFilter) params.append('reporter_search', searchFilter)
      params.append('sort_by', sortBy)
      params.append('sort_order', sortOrder)

      const response = await fetch(`/api/issues?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })

      if (!response.ok) {
        setError('Błąd podczas ładowania zgłoszeń')
        setIssues([])
        return
      }

      const list: IssueRow[] = await response.json()
      setIssues(list)
    } catch (err) {
      setError('Błąd połączenia z serwerem')
      setIssues([])
    } finally {
      setLoading(false)
    }
  }

  const openIssuesCreator = () => setShowCreator(true)

  const deleteIssue = async (issueId: string) => {
    const confirmed = await showConfirm('Potwierdzenie usunięcia', `Czy na pewno chcesz usunąć zgłoszenie ${issueId}?`)
    if (!confirmed) return
    try {
      const response = await fetch(`/api/issues/${issueId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        showSuccess('Zgłoszenie zostało usunięte pomyślnie')
        loadIssues()
      } else {
        const errorData = await response.json()
        showError('Błąd usuwania', errorData.detail || 'Błąd podczas usuwania zgłoszenia')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }



  const updateStatus = async (issueId: string, status: string) => {
    const statusLabels: { [key: string]: string } = {
      'utworzone': 'utworzone',
      'zaadresowane': 'zaadresowane',
      'w_trakcie_realizacji': 'w trakcie realizacji',
      'zrealizowane': 'zrealizowane',
      'zamkniete': 'zamknięte'
    }

    const confirmed = await showConfirm('Potwierdzenie zmiany statusu', `Czy na pewno chcesz zmienić status na "${statusLabels[status]}"?`)
    if (!confirmed) return

    try {
      const response = await fetch(`/api/issues/${issueId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status }),
      })
      if (response.ok) {
        showSuccess('Status zgłoszenia został zaktualizowany pomyślnie')
        loadIssues()

        // Pokaż modal z pytaniem o wysłanie e-maila
        setEmailModalType('update')
        setEmailUpdateType('status_change')
        setEmailNewStatus(status)
        setEmailIssueId(issueId)
        setShowEmailModal(true)
      } else {
        const errorData = await response.json()
        showError('Błąd aktualizacji statusu', errorData.detail || 'Błąd podczas aktualizacji statusu')
      }
    } catch {
      showError('Błąd połączenia', 'Nie można połączyć się z serwerem')
    }
  }

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortBy(field)
      setSortOrder('DESC')
    }
  }

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return '↕️'
    return sortOrder === 'ASC' ? '↑' : '↓'
  }

  // Podgląd zgłoszenia
  const openPreview = async (issueId: string) => {
    setPreviewOpen(true)
    setPreviewLoading(true)
    setPreviewIssue(null)
    setComments([])
    setShowCommentForm(false)
    setNewComment('')
    try {
      const resp = await fetch(`/api/issues/${issueId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (!resp.ok) throw new Error()
      const data: IssueDetail = await resp.json()
      setPreviewIssue(data)
      // Załaduj komentarze
      await loadComments(issueId)
    } catch {
      showError('Błąd', 'Nie udało się pobrać danych zgłoszenia do podglądu.')
      setPreviewOpen(false)
    } finally {
      setPreviewLoading(false)
    }
  }

  const closePreview = () => {
    setPreviewOpen(false)
    setPreviewIssue(null)
    setPreviewLoading(false)
    setComments([])
    setShowCommentForm(false)
    setNewComment('')
  }

  // Ładowanie komentarzy
  const loadComments = async (issueId: string) => {
    try {
      const resp = await fetch(`/api/comments?resource_type=issue&resource_id=${issueId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (resp.ok) {
        const commentsData = await resp.json()
        setComments(commentsData)
      }
    } catch (err) {
      console.error('Error loading comments:', err)
    }
  }

  // Dodawanie komentarza
  const addComment = async () => {
    if (!newComment.trim() || !previewIssue) return

    const commentContent = newComment.trim()
    setCommentLoading(true)
    try {
      const resp = await fetch('/api/comments', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          resource_type: 'issue',
          resource_id: previewIssue.id,
          content: commentContent
        })
      })

      if (resp.ok) {
        setNewComment('')
        setShowCommentForm(false)
        showSuccess('Komentarz został dodany pomyślnie')
        await loadComments(previewIssue.id)

        // Pokaż modal z pytaniem o wysłanie e-maila
        setEmailModalType('update')
        setEmailUpdateType('comment_added')
        setEmailCommentContent(commentContent)
        setEmailIssueId(previewIssue.id)
        setShowEmailModal(true)
      } else {
        const errorData = await resp.json()
        showError('Błąd', errorData.detail || 'Nie udało się dodać komentarza')
      }
    } catch (err) {
      showError('Błąd', 'Nie udało się dodać komentarza')
    } finally {
      setCommentLoading(false)
    }
  }

  // Email functions
  const handleEmailConfirm = () => {
    setShowEmailModal(false)
    setShowEmailFormModal(true)

    // Prepare email data
    if (emailModalType === 'update') {
      if (emailUpdateType === 'status_change') {
        setEmailFormData({
          to: '', // Will be filled by API
          subject: `Zgłoszenie ${emailIssueId}`,
          body: `Dzień dobry,

z przyjemnością informujemy, że Twoje zgłoszenie otrzymało nowy status ${getStatusLabel(emailNewStatus)}.

Z poważaniem

Utrzymanie sieci WOD-KAN Gmina Tarczyn`
        })
      } else if (emailUpdateType === 'comment_added') {
        const shortComment = emailCommentContent.length > 100
          ? emailCommentContent.substring(0, 100) + "..."
          : emailCommentContent
        setEmailFormData({
          to: '', // Will be filled by API
          subject: `Zgłoszenie ${emailIssueId}`,
          body: `Dzień dobry,

z przyjemnością informujemy, że Twoje zgłoszenie otrzymało nowy komentarz: "${shortComment}"

Z poważaniem

Utrzymanie sieci WOD-KAN Gmina Tarczyn`
        })
      }
    } else if (emailModalType === 'summary') {
      setEmailFormData({
        to: '', // Will be filled by API
        subject: `Podsumowanie zgłoszenia ${emailIssueId}`,
        body: 'Podsumowanie zostanie wygenerowane automatycznie...'
      })
    }
  }

  const handleEmailSend = async () => {
    setEmailSending(true)
    try {
      let endpoint = ''
      let params = new URLSearchParams()

      if (emailModalType === 'update') {
        endpoint = `/api/issues/${emailIssueId}/send-update-email`
        params.append('update_type', emailUpdateType)
        if (emailUpdateType === 'status_change') {
          params.append('new_status', emailNewStatus)
        } else if (emailUpdateType === 'comment_added') {
          params.append('comment_content', emailCommentContent)
        }
      } else if (emailModalType === 'summary') {
        endpoint = `/api/issues/${emailIssueId}/send-summary-email`
      }

      const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        showSuccess('E-mail został wysłany pomyślnie')
        setShowEmailFormModal(false)
      } else {
        const errorData = await response.json()
        showError('Błąd wysyłania e-maila', errorData.detail || 'Nie udało się wysłać e-maila')
      }
    } catch (error) {
      showError('Błąd wysyłania e-maila', 'Nie udało się wysłać e-maila')
    } finally {
      setEmailSending(false)
    }
  }

  const handleSendSummary = (issueId: string) => {
    setEmailModalType('summary')
    setEmailIssueId(issueId)
    setShowEmailModal(true)
  }

  const handleCreateService = (issueId: string) => {
    // Navigate to services page with issue ID parameter
    window.location.href = `/services?createFromIssue=${issueId}`
  }



  const getIssueTypeLabel = (issueType: string) => {
    const labels = {
      'brak_wody': 'Brak wody w budynku',
      'spadek_cisnienia': 'Spadek ciśnienia w instalacji',
      'wycieki': 'Wycieki na sieci wodociągowej',
      'cofka': 'Cofka / wybicie ścieków',
      'niedroznosc': 'Niedrożność kanalizacji',
      'inne': 'Inne'
    }
    return labels[issueType as keyof typeof labels] || issueType
  }

  const getStatusLabel = (status: string) => {
    const labels = {
      'utworzone': 'Utworzone',
      'zaadresowane': 'Zaadresowane',
      'w_trakcie_realizacji': 'W trakcie realizacji',
      'zrealizowane': 'Zrealizowane',
      'zamkniete': 'Zamknięte'
    }
    return labels[status as keyof typeof labels] || status
  }

  if (loading && issues.length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Ładowanie zgłoszeń...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Zgłoszenia</h2>
        <button
          onClick={openIssuesCreator}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
        >
          + Nowe zgłoszenie
        </button>
      </div>

      {/* Filtry */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Filtry</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Klient */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Klient</label>
            <select
              value={clientFilterInput}
              onChange={(e) => setClientFilterInput(e.target.value === '' ? '' : parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszyscy klienci</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
          </div>

          {/* Data od */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data od</label>
            <input
              type="date"
              value={dateFromFilterInput}
              onChange={(e) => setDateFromFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Data do */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data do</label>
            <input
              type="date"
              value={dateToFilterInput}
              onChange={(e) => setDateToFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Typ zgłoszenia */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Typ zgłoszenia</label>
            <select
              value={issueTypeFilterInput}
              onChange={(e) => setIssueTypeFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="brak_wody">Brak wody w budynku</option>
              <option value="spadek_cisnienia">Spadek ciśnienia w instalacji</option>
              <option value="wycieki">Wycieki na sieci wodociągowej</option>
              <option value="cofka">Cofka / wybicie ścieków</option>
              <option value="niedroznosc">Niedrożność kanalizacji</option>
              <option value="inne">Inne</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilterInput}
              onChange={(e) => setStatusFilterInput(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Wszystkie</option>
              <option value="utworzone">Utworzone</option>
              <option value="zaadresowane">Zaadresowane</option>
              <option value="w_trakcie_realizacji">W trakcie realizacji</option>
              <option value="zrealizowane">Zrealizowane</option>
              <option value="zamkniete">Zamknięte</option>
            </select>
          </div>

          {/* Szukaj zgłaszającego */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Szukaj zgłaszającego</label>
            <input
              type="text"
              value={searchFilterInput}
              onChange={(e) => setSearchFilterInput(e.target.value)}
              placeholder="Imię i nazwisko..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Przyciski */}
          <div className="flex items-end gap-2">
            <button
              onClick={handleFilterClick}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
            >
              Filtruj
            </button>
            <button
              onClick={clearFilters}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium"
            >
              Wyczyść filtry
            </button>
          </div>
        </div>
      </div>

      {/* Błąd */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      {/* Tabela zgłoszeń */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div
          className="overflow-x-auto"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {/* Data utworzenia */}
                <th
                  className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-20 sm:w-auto"
                  onClick={() => handleSort('date')}
                >
                  <span className="hidden sm:inline">Data utworzenia </span>
                  <span className="sm:hidden">Data </span>
                  {getSortIcon('date')}
                </th>

                {/* Numer (ID) */}
                <th
                  className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 w-16 sm:w-auto"
                  onClick={() => handleSort('id')}
                >
                  Numer {getSortIcon('id')}
                </th>

                {/* Typ */}
                <th
                  className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('issue_type')}
                >
                  Typ {getSortIcon('issue_type')}
                </th>

                {/* Zgłaszający */}
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Zgłaszający
                </th>

                {/* Status */}
                <th
                  className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  Status {getSortIcon('status')}
                </th>

                {/* Akcje */}
                <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 sm:w-auto">
                  Akcje
                </th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {issues.map((issue) => (
                <tr key={issue.id} className="hover:bg-gray-50">
                  {/* Data */}
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="sm:hidden font-medium">
                      {format(new Date(issue.date), 'dd.MM', { locale: pl })}
                    </span>
                    <span className="hidden sm:inline">
                      {format(new Date(issue.date), 'dd.MM.yyyy', { locale: pl })}
                    </span>
                  </td>

                  {/* Numer */}
                  <td className={`px-2 sm:px-6 py-4 whitespace-nowrap text-sm font-medium ${
                    issue.readonly ? 'text-green-600' : 'text-blue-500'
                  }`}>
                    <button
                      onClick={() => openPreview(issue.id)}
                      className="inline-block px-2 py-1 rounded border-2 hover:bg-gray-100 transition-colors"
                      title="Kliknij aby zobaczyć podgląd zgłoszenia"
                    >
                      {issue.id}
                    </button>
                    {issue.is_external && (
                      <span className="ml-1 text-xs bg-purple-100 text-purple-800 px-1 rounded">
                        EXT
                      </span>
                    )}
                  </td>

                  {/* Typ */}
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getIssueTypeLabel(issue.issue_type)}
                  </td>

                  {/* Zgłaszający */}
                  <td className="px-2 sm:px-6 py-4 text-sm text-gray-900">
                    <div className="max-w-xs">
                      <div className="truncate font-medium">{issue.reporter_name}</div>
                      <div className="text-xs text-gray-500">{issue.reporter_phone}</div>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getStatusLabel(issue.status)}
                  </td>

                  {/* Akcje */}
                  <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={() => {
                          setEditingIssueId(issue.id)
                          setShowCreator(true)
                        }}
                        disabled={issue.readonly}
                        className={`px-2 py-1 rounded text-xs ${
                          issue.readonly
                            ? 'text-gray-400 cursor-not-allowed bg-gray-100'
                            : 'text-blue-600 hover:text-blue-900 hover:bg-blue-50'
                        }`}
                      >
                        Edytuj
                      </button>

                      {/* Status change buttons for all users */}
                      {!issue.readonly && issue.status === 'utworzone' && (
                        <button
                          onClick={() => updateStatus(issue.id, 'zaadresowane')}
                          className="px-2 py-1 rounded text-xs text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50"
                        >
                          Zaadresuj
                        </button>
                      )}

                      {issue.status === 'zaadresowane' && (
                        <button
                          onClick={() => updateStatus(issue.id, 'w_trakcie_realizacji')}
                          className="px-2 py-1 rounded text-xs text-orange-600 hover:text-orange-900 hover:bg-orange-50"
                        >
                          W trakcie
                        </button>
                      )}

                      {issue.status === 'w_trakcie_realizacji' && (
                        <button
                          onClick={() => updateStatus(issue.id, 'zrealizowane')}
                          className="px-2 py-1 rounded text-xs text-green-600 hover:text-green-900 hover:bg-green-50"
                        >
                          Zrealizuj
                        </button>
                      )}

                      {issue.status === 'zrealizowane' && (
                        <button
                          onClick={() => updateStatus(issue.id, 'zamkniete')}
                          className="px-2 py-1 rounded text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                        >
                          Zamknij
                        </button>
                      )}

                      {/* Send summary email button */}
                      <button
                        onClick={() => handleSendSummary(issue.id)}
                        className="px-2 py-1 rounded text-xs text-green-600 hover:text-green-900 hover:bg-green-50"
                        title="Wyślij podsumowanie zgłoszenia e-mailem"
                      >
                        📧 Podsumowanie
                      </button>

                      {/* Create service button */}
                      <button
                        onClick={() => handleCreateService(issue.id)}
                        className="px-2 py-1 rounded text-xs text-purple-600 hover:text-purple-900 hover:bg-purple-50"
                        title="Utwórz zlecenie na podstawie tego zgłoszenia"
                      >
                        🔧 Utwórz zlecenie
                      </button>

                      {/* Delete action - admin can delete any issue, regular users only non-readonly */}
                      {(!issue.readonly) && (
                        <button
                          onClick={() => deleteIssue(issue.id)}
                          className="px-2 py-1 rounded text-xs text-red-600 hover:text-red-900 hover:bg-red-50"
                        >
                          Usuń
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {issues.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            Brak zgłoszeń spełniających kryteria wyszukiwania
          </div>
        )}
      </div>

      {/* Podsumowanie */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex justify-between items-center text-sm text-blue-800">
          <span>Znaleziono zgłoszeń: <strong>{issues.length}</strong></span>
        </div>
      </div>

      {/* Modal kreatora/edycji */}
      <IssuesCreator
        token={token}
        isOpen={showCreator}
        onClose={() => {
          setShowCreator(false)
          setEditingIssueId(undefined)
        }}
        onIssueCreated={loadIssues}
        issueId={editingIssueId}
      />

      {/* Modal PODGLĄDU */}
      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Podgląd zgłoszenia</h3>
              <button onClick={closePreview} className="text-gray-400 hover:text-gray-600" aria-label="Zamknij">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="p-6 max-h-[80vh] overflow-y-auto">
              {previewLoading && (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2">Ładowanie…</span>
                </div>
              )}

              {!previewLoading && previewIssue && (
                <div className="bg-white mx-auto shadow print:shadow-none">
                  <div className="mx-auto bg-white border border-gray-200 rounded-md p-8 w-[900px]">
                    {/* Nagłówek */}
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <h1 className="text-2xl font-bold text-gray-900">{getIssueTypeLabel(previewIssue.issue_type)}</h1>
                        <p className="text-sm text-gray-600">Nr: <span className="font-medium text-gray-900">{previewIssue.id}</span></p>
                        {previewIssue.is_external && (
                          <span className="inline-block mt-1 text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                            Zgłoszenie zewnętrzne
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Data zgłoszenia</p>
                        <p className="text-base font-medium text-gray-900">
                          {format(new Date(previewIssue.date), 'dd.MM.yyyy', { locale: pl })}
                        </p>
                      </div>
                    </div>

                    {/* Dane podstawowe */}
                    <div className="grid grid-cols-2 gap-6 mb-6">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Klient</div>
                        <div className="text-base font-medium text-gray-900">{previewIssue.client_name}</div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Autor</div>
                        <div className="text-base font-medium text-gray-900">{previewIssue.author_name || '(brak danych)'}</div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Status</div>
                        <div className="text-base font-medium text-gray-900">{getStatusLabel(previewIssue.status)}</div>
                      </div>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Typ awarii</div>
                        <div className="text-base font-medium text-gray-900">{getIssueTypeLabel(previewIssue.issue_type)}</div>
                      </div>
                    </div>

                    {/* Dane zgłaszającego */}
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-3">Dane zgłaszającego</h2>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-gray-500">Imię i nazwisko</div>
                          <div className="text-base font-medium text-gray-900">{previewIssue.reporter_name}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Telefon kontaktowy</div>
                          <div className="text-base font-medium text-gray-900">{previewIssue.reporter_phone}</div>
                        </div>
                      </div>
                    </div>

                    {/* Lokalizacja awarii */}
                    <div className="mb-6">
                      <h2 className="text-lg font-semibold text-gray-900 mb-3">Lokalizacja awarii</h2>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-gray-500">Miejscowość</div>
                          <div className="text-base font-medium text-gray-900">{previewIssue.location || '-'}</div>
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">Ulica/nr działki</div>
                          <div className="text-base font-medium text-gray-900">{previewIssue.street_address}</div>
                        </div>
                        <div className="col-span-2">
                          <div className="text-sm text-gray-500">Czy dotyczy części wspólnych?</div>
                          <div className="text-base font-medium text-gray-900">
                            {previewIssue.is_common_area ? 'Tak - instalacja wewnętrzna' : 'Nie - części wspólne'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Dodatkowe informacje */}
                    {previewIssue.additional_info && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-2">Dodatkowe informacje</h2>
                        <div className="whitespace-pre-wrap text-gray-900">{previewIssue.additional_info}</div>
                      </div>
                    )}

                    {/* Obrazy */}
                    {!!previewIssue.images?.length && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3">Załączone obrazy</h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {previewIssue.images.map((img) => (
                            <div key={img.id} className="border border-gray-200 rounded-md overflow-hidden">
                              <img
                                src={`/${img.file_path}`}
                                alt={img.original_filename}
                                className="w-full h-32 object-cover"
                              />
                              <div className="p-2">
                                <div className="text-xs text-gray-600 truncate">{img.original_filename}</div>
                                {img.description && (
                                  <div className="text-xs text-gray-500 mt-1">{img.description}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Historia zmian statusu */}
                    {!!previewIssue.status_history?.length && (
                      <div className="mb-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-3">Historia zmian statusu</h2>
                        <div className="space-y-2">
                          {previewIssue.status_history.map((h) => (
                            <div key={h.id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
                              <div>
                                <span className="text-gray-600">
                                  {h.old_status ? `${getStatusLabel(h.old_status)} → ` : ''}
                                </span>
                                <span className="font-medium text-gray-900">{getStatusLabel(h.new_status)}</span>
                              </div>
                              <div className="text-right text-gray-500">
                                <div>{format(new Date(h.changed_at), 'dd.MM.yyyy HH:mm', { locale: pl })}</div>
                                <div className="text-xs">przez {h.changed_by_name}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Komentarze */}
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h2 className="text-lg font-semibold text-gray-900">Komentarze</h2>
                        {!previewIssue.readonly && (
                          <button
                            onClick={() => setShowCommentForm(!showCommentForm)}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm font-medium"
                          >
                            {showCommentForm ? 'Anuluj' : '+ Dodaj komentarz'}
                          </button>
                        )}
                      </div>

                      {/* Formularz dodawania komentarza */}
                      {showCommentForm && !previewIssue.readonly && (
                        <div className="mb-4 p-4 border border-gray-200 rounded-md bg-gray-50">
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Wpisz swój komentarz..."
                            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                            rows={3}
                            maxLength={2000}
                          />
                          <div className="flex justify-between items-center mt-2">
                            <span className="text-xs text-gray-500">
                              {newComment.length}/2000 znaków
                            </span>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setShowCommentForm(false)
                                  setNewComment('')
                                }}
                                className="px-3 py-1 text-sm bg-gray-300 hover:bg-gray-400 rounded-md"
                              >
                                Anuluj
                              </button>
                              <button
                                onClick={addComment}
                                disabled={!newComment.trim() || commentLoading}
                                className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {commentLoading ? 'Dodawanie...' : 'Dodaj'}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Lista komentarzy */}
                      {comments.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          Brak komentarzy
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {comments.map((comment) => (
                            <div key={comment.id} className="border border-gray-200 rounded-md p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{comment.user_full_name}</span>
                                  <span className="text-xs text-gray-500">
                                    {format(new Date(comment.created_at), 'dd.MM.yyyy HH:mm', { locale: pl })}
                                  </span>
                                </div>
                              </div>
                              <div className="text-gray-900 whitespace-pre-wrap">{comment.content}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t">
              <button onClick={closePreview} className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md">
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Modal - Question */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">Wysyłanie aktualizacji</h3>
              <button onClick={() => setShowEmailModal(false)} className="text-gray-400 hover:text-gray-600" aria-label="Zamknij">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-700 mb-6">
                Czy chcesz wysłać e-mail z aktualizacją zgłoszenia do autora?
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="flex-1 px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Nie
                </button>
                <button
                  onClick={handleEmailConfirm}
                  className="flex-1 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md"
                >
                  Tak, wyślij
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Form Modal */}
      {showEmailFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-xl font-semibold text-gray-900">
                {emailModalType === 'update' ? 'Aktualizacja zgłoszenia' : 'Podsumowanie zgłoszenia'}
              </h3>
              <button onClick={() => setShowEmailFormModal(false)} className="text-gray-400 hover:text-gray-600" aria-label="Zamknij">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Temat</label>
                  <input
                    type="text"
                    value={emailFormData.subject}
                    onChange={(e) => setEmailFormData({ ...emailFormData, subject: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Treść</label>
                  <textarea
                    value={emailFormData.body}
                    onChange={(e) => setEmailFormData({ ...emailFormData, body: e.target.value })}
                    rows={12}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t">
              <button
                onClick={() => setShowEmailFormModal(false)}
                className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Anuluj
              </button>
              <button
                onClick={handleEmailSend}
                disabled={emailSending}
                className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
              >
                {emailSending ? 'Wysyłanie...' : 'Wyślij e-mail'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
